import { buildBoardColumns } from './boardLens';
import { columnsForSection } from './sectionPipeline';
import type { BoardColumn } from './boardLens';
import type { SectionStatusMap } from './sectionPipeline';
import type { ProjectStatus } from '../projects/types';
import type { SavedViewSort } from '../savedViews/types';
import type { Section } from '../sections/types';
import type { Task } from '../tasks/types';

/**
 * Sections as rows, each with its own pipeline across.
 *
 * ── The problem this fixes ─────────────────────────────────────────────────
 *
 * Grouping a board by Section turned the SECTIONS into the columns: Long Form,
 * Short Form, Facebook, side by side, each a single undifferentiated pile. The
 * pipeline disappeared entirely — you could see which bucket a piece was in
 * and nothing about how far along it was.
 *
 * Grouping by Status did the opposite: one pipeline for the whole project,
 * with Long Form and Facebook work interleaved in every column.
 *
 * Neither answers "where is my short-form work up to", which is the question.
 * Swimlanes answer both at once: the row says which kind of work, the column
 * says how far along.
 *
 * ── Each row draws its OWN columns ─────────────────────────────────────────
 *
 * Not the project's full stage list. A Facebook graphic does not go through
 * Scripting or Filming, and drawing those columns on its row spreads the three
 * that matter thin between five that never fill.
 *
 * Which columns a section draws is section_statuses, the same configuration
 * the focused view already uses — so a section cannot show one pipeline in one
 * view and a different one in the other. columnsForSection also keeps any
 * column that HOLDS tasks whatever the configuration says, which is what makes
 * narrowing a section reversible rather than destructive.
 */

export interface Swimlane {
  /** Stable render key. The section id, or 'none' for the unsectioned lane. */
  key: string;
  name: string;
  /** Null on the unsectioned lane — a drop there clears the task's section. */
  sectionId: string | null;
  columns: BoardColumn[];
  taskCount: number;
}

/** The lane for tasks nobody has filed. Same wording as the grouped board. */
export const UNSECTIONED_LANE_NAME = 'No section';

/**
 * One lane per section, plus the unsectioned lane when it has anything in it.
 *
 * The empty unsectioned lane is dropped, and empty SECTION lanes are kept. The
 * asymmetry is deliberate: a section is a thing somebody made and expects to
 * see, and its emptiness is information — "nothing in Facebook this week" is
 * an answer. "No section" is not a place anybody chose to put work, so an
 * empty one is just a row of nothing.
 */
export function swimlanes(
  sections: Section[],
  tasks: Task[],
  statuses: ProjectStatus[],
  configured: SectionStatusMap,
  sort: SavedViewSort,
): Swimlane[] {
  const ordered = sections.slice().sort((left, right) => left.position - right.position);

  const lanes: Swimlane[] = ordered.map((section) => {
    const mine = tasks.filter((task) => task.section_id === section.id);
    return {
      key: section.id,
      name: section.name,
      sectionId: section.id,
      // The section's own columns, from the same configuration the focused
      // view reads. Two views computing this differently is how one section
      // ends up with two pipelines.
      columns: buildBoardColumns(
        mine,
        columnsForSection(statuses, configured, mine, section.id),
        sort,
      ),
      taskCount: mine.length,
    };
  });

  const known = new Set(ordered.map((section) => section.id));
  const loose = tasks.filter((task) => task.section_id === null || !known.has(task.section_id));
  if (loose.length > 0) {
    lanes.push({
      key: 'none',
      name: UNSECTIONED_LANE_NAME,
      sectionId: null,
      // Every column, because no single section's preferences apply to work
      // that belongs to none of them.
      columns: buildBoardColumns(loose, statuses, sort),
      taskCount: loose.length,
    });
  }

  return lanes;
}
